import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { ActionButtonsWrapper, ActionButton } from '../styles'
import type {
  AnyVisualization,
  ButtonsPanelButton,
  ButtonsPanelButtonVariant,
} from '../visualization.types'
import Modal from '~/prix/react/components/modal'
import ModalSpaceItinerary from '../../modalSpaceItinerary.component'

const Container = styled.div`
  padding: 0 16px 16px 16px;
`

interface ButtonPanelProps {
  visualization: Extract<AnyVisualization, { renderer: 'buttons' }>
  onButtonClick?: (button: ButtonsPanelButton) => void
  isExpanded?: boolean
  isMaximized?: boolean
}

const resolveVariant = (button: ButtonsPanelButton): ButtonsPanelButtonVariant => {
  if (button.variant) return button.variant
  if (button.type === 'export' || button.type === 'component') return 'primary'
  return 'neutral'
}

export default function ButtonsPanel({
  visualization,
  isExpanded,
  isMaximized,
  onButtonClick,
}: ButtonPanelProps) {
  const navigate = useNavigate()

  const buttons = visualization.payload.buttons

  const [activatedComponent, setActivatedComponent] = useState<string | undefined>()

  const handleCloseModal = () => {
    setActivatedComponent(undefined)
  }

  const handleButtonClick = (button: ButtonsPanelButton | null) => {
    if (!button || button.disabled) return

    if (button.type === 'component') {
      setActivatedComponent(button.key)
    } else {
      setActivatedComponent(undefined)
    }

    if (button.type === 'route') {
      navigate(button.path)
    }

    onButtonClick?.(button)
  }

  return (
    <Container>
      <ActionButtonsWrapper>
        {buttons.map(button => {
          const variant = resolveVariant(button)

          return (
            <ActionButton
              key={button.label}
              $variant={variant}
              $isMaximized={isMaximized}
              $isExpanded={isExpanded}
              disabled={Boolean(button.disabled)}
              onClick={() => handleButtonClick(button)}
            >
              <span>{button.label}</span>
            </ActionButton>
          )
        })}
      </ActionButtonsWrapper>

      {(() => {
        const componentButton = buttons.find(
          b => b.type === 'component' && (b as any).key === 'itineraryComponent',
        )

        if (!componentButton) return null

        return (
          <Modal
            isOpen={activatedComponent === 'itineraryComponent'}
            onClose={handleCloseModal}
            modalStyle='sentinel'
          >
            <ModalSpaceItinerary
              onCloseModal={handleCloseModal}
              filter={(componentButton as any).params.filter}
              {...(componentButton as any).params}
            />
          </Modal>
        )
      })()}
    </Container>
  )
}
